"use client"

import { Users, User, ChevronRight } from "lucide-react"

interface RecentUser {
  id: string
  username: string
  avatarUrl?: string | null
  isOnline: boolean
  lastOnline?: string | null
}

interface RecentActiveUsersProps {
  users: RecentUser[]
  currentUserId?: string
  showTitle?: boolean
  showScrollHint?: boolean
  skeletonCount?: number
}

// Funkcja do kapitalizacji pierwszej litery
const capitalizeFirstLetter = (str: string) => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// Funkcja formatowania aktywności
const formatLastActive = (lastOnline: string | null | undefined, isOnline: boolean) => {
  if (isOnline) {
    return { text: "Online", color: "text-green-600 dark:text-green-400", dotColor: "bg-green-500" }
  }

  if (!lastOnline) {
    return { text: "Brak danych", color: "text-gray-500 dark:text-zinc-400", dotColor: "bg-gray-400" }
  }

  const now = new Date()
  const lastActiveDate = new Date(lastOnline)
  const diffMs = now.getTime() - lastActiveDate.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) {
    return { text: "Przed chwilą", color: "text-green-600 dark:text-green-400", dotColor: "bg-green-500" }
  } else if (diffMins < 60) {
    return { text: `${diffMins} min`, color: "text-yellow-600 dark:text-yellow-400", dotColor: "bg-yellow-500" }
  } else if (diffHours < 24) {
    return { text: `${diffHours}h`, color: "text-orange-600 dark:text-orange-400", dotColor: "bg-orange-500" }
  } else if (diffDays === 1) {
    return { text: "Wczoraj", color: "text-gray-600 dark:text-zinc-400", dotColor: "bg-gray-400" }
  } else if (diffDays < 7) {
    return { text: `${diffDays}d`, color: "text-gray-600 dark:text-zinc-400", dotColor: "bg-gray-400" }
  } else {
    return { text: lastActiveDate.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }), color: "text-gray-500 dark:text-zinc-500", dotColor: "bg-gray-400" }
  }
}

export default function RecentActiveUsers({
  users,
  currentUserId,
  showTitle = true,
  showScrollHint = false,
  skeletonCount = 6
}: RecentActiveUsersProps) {
  // Filtruj użytkowników (usuń aktualnego użytkownika)
  const filteredUsers = currentUserId
    ? users.filter(u => String(u.id) !== String(currentUserId))
    : users

  // Jeśli nie ma użytkowników, pokaż skeletony
  const displayItems = filteredUsers.length === 0
    ? Array.from({ length: skeletonCount })
    : filteredUsers

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 p-4 h-full flex flex-col">
      {showTitle && (
        <div className="flex items-center justify-between mb-3 lg:mb-4 flex-shrink-0">
          <h3 className="text-sm lg:text-base font-semibold text-gray-900 dark:text-zinc-100 flex items-center">
            <Users className="w-4 h-4 lg:w-5 lg:h-5 mr-2 text-blue-500" />
            Ostatnio aktywni
          </h3>
          {showScrollHint && (
            <span className="text-xs text-gray-500 dark:text-zinc-400 flex items-center lg:hidden">
              Przesuń <ChevronRight className="w-3 h-3 ml-1" />
            </span>
          )}
        </div>
      )}

      {/* Horizontal scroll container */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory -webkit-overflow-scrolling-touch flex-1 items-center min-h-0">
        {(displayItems as (RecentUser | undefined)[]).map((user, idx) => {
          if (!user) {
            // Placeholder skeleton avatar
            return (
              <div key={"skeleton-" + idx} className="flex flex-col items-center justify-center gap-2 min-w-[80px] snap-start animate-pulse">
                <div className="relative">
                  <div className="w-16 h-16 rounded-xl border-2 border-gray-200 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-gray-300 dark:bg-zinc-700 rounded-xl border-2 border-white dark:border-zinc-900" />
                </div>
                <div className="text-center">
                  <div className="h-4 w-12 bg-gray-200 dark:bg-zinc-700 rounded mb-1 mx-auto" />
                  <div className="h-3 w-8 bg-gray-100 dark:bg-zinc-800 rounded mx-auto" />
                </div>
              </div>
            )
          }

          const activity = formatLastActive(user.lastOnline, user.isOnline)

          return (
            <div
              key={user.id}
              className="flex flex-col items-center justify-center gap-2 min-w-[80px] snap-start transition-all duration-500 opacity-100"
            >
              {/* Avatar z kropką statusu */}
              <div className="relative">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center  bg-white dark:bg-zinc-800">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.username}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-zinc-800 dark:to-zinc-700 flex items-center justify-center">
                      <User className="h-8 w-8 text-gray-600 dark:text-zinc-400" />
                    </div>
                  )}
                </div>
                {/* Status dot */}
                <div className={`absolute bottom-0 right-0 w-4 h-4 ${activity.dotColor} rounded-full border-2 border-white dark:border-zinc-900 transition-all duration-500`}></div>
              </div>

              {/* Username */}
              <div className="text-center">
                <p className="text-xs font-medium text-gray-900 dark:text-zinc-100 truncate max-w-[80px] transition-all duration-500">
                  {user.username ? capitalizeFirstLetter(user.username) : null}
                </p>
                <p className={`text-xs ${activity.color} truncate max-w-[80px] transition-all duration-500`}>
                  {activity.text}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
